/**
 * cues-weapons.ts — procedural weapon SFX builders (v2M8.4).
 *
 * Implements AUDIO §6's per-damage-type weapon cues (4 categories ×
 * 3 events = 12 cues). Each builder reads the third `variation` arg
 * to apply pitch / gain modulation per AUDIO §6.1 — variation values
 * are derived deterministically from sim tick + ship id by the diff
 * path so replays produce identical audio.
 *
 * Events per damage type:
 *   - **fire** — the muzzle event when the firing ship's cooldown expires.
 *   - **shield** — a hit absorbed by the target's shields.
 *   - **hull** — a hit landed on the target's hull.
 *
 * Genre treatments (concise per AUDIO §6 table):
 *   - Energy: rising synth sweep / sizzle + bell / dry crackle + thud.
 *   - Kinetic: percussive thump + metallic tail / clang + ring / impact + scrape.
 *   - Concussion: low-freq whump / diffuse boom (hull = shield in v1
 *     since splash is deferred per MECHANICS).
 *   - EMP: arc crackle / static burst + warble / static one-shot
 *     (looping-for-disable-duration variant flagged for v3M11 polish).
 *
 * All cues route through the SFX bus. Pan + attenuation are applied by
 * the engine post-source-chain via PlayCueOpts.panX / attenuation —
 * the diff path computes them from camera-relative ship position.
 */

import type { AudioEngine } from './engine';
import type { CueVariation } from './registry';

/** Register all 12 §6 weapon cues against the engine, plus the four
 *  v3M28.5 missile-specific cues. */
export function registerWeaponCues(engine: AudioEngine): void {
  engine.registry.registerProcedural('weapon:energy:fire', 'sfx', buildEnergyFire);
  engine.registry.registerProcedural('weapon:energy:shield', 'sfx', buildEnergyShield);
  engine.registry.registerProcedural('weapon:energy:hull', 'sfx', buildEnergyHull);
  engine.registry.registerProcedural('weapon:kinetic:fire', 'sfx', buildKineticFire);
  engine.registry.registerProcedural('weapon:kinetic:shield', 'sfx', buildKineticShield);
  engine.registry.registerProcedural('weapon:kinetic:hull', 'sfx', buildKineticHull);
  engine.registry.registerProcedural('weapon:concussion:fire', 'sfx', buildConcussionFire);
  engine.registry.registerProcedural('weapon:concussion:shield', 'sfx', buildConcussionImpact);
  engine.registry.registerProcedural('weapon:concussion:hull', 'sfx', buildConcussionImpact);
  engine.registry.registerProcedural('weapon:emp:fire', 'sfx', buildEmpFire);
  engine.registry.registerProcedural('weapon:emp:shield', 'sfx', buildEmpImpact);
  engine.registry.registerProcedural('weapon:emp:hull', 'sfx', buildEmpImpact);
  // v3M28.5 — per-family missile launch + impact. Procedural
  // placeholders re-use the concussion fire/impact builders with
  // per-family pitch + gain variation so playtest can distinguish
  // Pulson (higher / lighter) from Zeon (lower / heavier).
  //
  // Procedural fallbacks are registered synchronously here so cues
  // are audible from boot. `loadMissileSamples` (called from main.ts)
  // race-replaces them with the commissioned ElevenLabs MP3s once
  // decoded — typically within a few hundred ms of boot, before the
  // first missile of a session fires. If the fetch fails for any
  // reason, the procedural cue stays active and the engine never
  // breaks the cue path.
  engine.registry.registerProcedural('weapon:missile:launch:pulson', 'sfx', buildPulsonLaunch);
  engine.registry.registerProcedural('weapon:missile:launch:zeon', 'sfx', buildZeonLaunch);
  engine.registry.registerProcedural('weapon:missile:impact:pulson', 'sfx', buildPulsonImpact);
  engine.registry.registerProcedural('weapon:missile:impact:zeon', 'sfx', buildZeonImpact);
}

/**
 * v3M28.6 — kick off async load + decode for the four commissioned
 * missile SFX. Caller (main.ts) invokes this once at boot, after
 * `registerWeaponCues` has registered the procedural fallbacks. The
 * returned Promise resolves when all four samples are decoded and
 * registered against their cue ids, but consumers don't typically
 * await it — fire-and-forget is fine, since the procedural fallback
 * stays active until the swap lands. Tests can await for determinism.
 *
 * Sample files live in `public/audio/cues/missile/` (served at
 * `/audio/cues/missile/<name>.mp3`) — same pattern as the missile
 * sprites under `public/vfx/missile/`. The MP3s are committed to git
 * as part of the v3M28.6 commit.
 */
export async function loadMissileSamples(engine: AudioEngine): Promise<void> {
  await Promise.all([
    engine.loadSample(
      'weapon:missile:launch:pulson',
      'sfx',
      '/audio/cues/missile/pulson-launch.mp3',
    ),
    engine.loadSample(
      'weapon:missile:launch:zeon',
      'sfx',
      '/audio/cues/missile/zeon-launch.mp3',
    ),
    engine.loadSample(
      'weapon:missile:impact:pulson',
      'sfx',
      '/audio/cues/missile/pulson-impact.mp3',
    ),
    engine.loadSample(
      'weapon:missile:impact:zeon',
      'sfx',
      '/audio/cues/missile/zeon-impact.mp3',
    ),
  ]);
}

// ── v3M28.5 missile cues ─────────────────────────────────────────────
//
// Each builder applies a per-family variation on top of the caller's
// CueVariation: Pulson goes pitch-up + gain-down (lighter, sharper);
// Zeon goes pitch-down + gain-up (heavier, slower decay). Both reuse
// the existing concussion fire/impact builders' source-graph topology
// so the registry stays maintainable — when the ElevenLabs assets
// arrive in v3M28.6 we replace `registerProcedural` with
// `registerSample` for these four ids and the diff layer keeps
// emitting the same cue ids untouched.

function withFamilyVariation(
  base: CueVariation,
  pitchMul: number,
  gainMul: number,
): CueVariation {
  return {
    pitchMul: base.pitchMul * pitchMul,
    gainMul: base.gainMul * gainMul,
  };
}

function buildPulsonLaunch(
  ctx: AudioContext,
  target: AudioNode,
  variation: CueVariation = NEUTRAL,
): AudioScheduledSourceNode {
  return buildConcussionFire(ctx, target, withFamilyVariation(variation, 1.45, 0.7));
}

function buildZeonLaunch(
  ctx: AudioContext,
  target: AudioNode,
  variation: CueVariation = NEUTRAL,
): AudioScheduledSourceNode {
  return buildConcussionFire(ctx, target, withFamilyVariation(variation, 0.7, 1.2));
}

function buildPulsonImpact(
  ctx: AudioContext,
  target: AudioNode,
  variation: CueVariation = NEUTRAL,
): AudioScheduledSourceNode {
  return buildConcussionImpact(ctx, target, withFamilyVariation(variation, 1.3, 0.75));
}

function buildZeonImpact(
  ctx: AudioContext,
  target: AudioNode,
  variation: CueVariation = NEUTRAL,
): AudioScheduledSourceNode {
  return buildConcussionImpact(ctx, target, withFamilyVariation(variation, 0.65, 1.25));
}

const NEUTRAL: CueVariation = { pitchMul: 1, gainMul: 1 };

// ── Energy ───────────────────────────────────────────────────────────

function buildEnergyFire(
  ctx: AudioContext,
  target: AudioNode,
  variation: CueVariation = NEUTRAL,
): AudioScheduledSourceNode {
  const now = ctx.currentTime;
  // Rising synth sweep 300 → 1200 Hz over 130 ms, low-passed sawtooth.
  const osc = ctx.createOscillator();
  osc.type = 'sawtooth';
  osc.frequency.setValueAtTime(300 * variation.pitchMul, now);
  osc.frequency.exponentialRampToValueAtTime(1200 * variation.pitchMul, now + 0.13);
  const filter = ctx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.value = 2400;
  filter.Q.value = 1;
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.0001, now);
  gain.gain.exponentialRampToValueAtTime(0.3 * variation.gainMul, now + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.15);
  osc.connect(filter);
  filter.connect(gain);
  gain.connect(target);
  osc.start(now);
  osc.stop(now + 0.16);
  return osc;
}

function buildEnergyShield(
  ctx: AudioContext,
  target: AudioNode,
  variation: CueVariation = NEUTRAL,
): AudioScheduledSourceNode {
  const now = ctx.currentTime;
  // Primary: high-freq sizzle (high-passed noise).
  const noise = createNoiseSource(ctx, 0.18);
  const sizzleFilter = ctx.createBiquadFilter();
  sizzleFilter.type = 'highpass';
  sizzleFilter.frequency.value = 3000 * variation.pitchMul;
  const sizzleGain = ctx.createGain();
  sizzleGain.gain.setValueAtTime(0.0001, now);
  sizzleGain.gain.exponentialRampToValueAtTime(0.22 * variation.gainMul, now + 0.005);
  sizzleGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.18);
  noise.connect(sizzleFilter);
  sizzleFilter.connect(sizzleGain);
  sizzleGain.connect(target);
  // Secondary: soft bell tone for the "energy contained by shield" feel.
  const bell = ctx.createOscillator();
  bell.type = 'sine';
  bell.frequency.value = 880 * variation.pitchMul;
  const bellGain = ctx.createGain();
  bellGain.gain.setValueAtTime(0.0001, now);
  bellGain.gain.exponentialRampToValueAtTime(0.18 * variation.gainMul, now + 0.01);
  bellGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.2);
  bell.connect(bellGain);
  bellGain.connect(target);
  bell.start(now);
  bell.stop(now + 0.21);
  return noise;
}

function buildEnergyHull(
  ctx: AudioContext,
  target: AudioNode,
  variation: CueVariation = NEUTRAL,
): AudioScheduledSourceNode {
  const now = ctx.currentTime;
  // Primary: dry crackle (short band-passed noise).
  const noise = createNoiseSource(ctx, 0.06);
  const crackleFilter = ctx.createBiquadFilter();
  crackleFilter.type = 'bandpass';
  crackleFilter.frequency.value = 1200 * variation.pitchMul;
  crackleFilter.Q.value = 2;
  const crackleGain = ctx.createGain();
  crackleGain.gain.setValueAtTime(0.0001, now);
  crackleGain.gain.exponentialRampToValueAtTime(0.25 * variation.gainMul, now + 0.005);
  crackleGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.07);
  noise.connect(crackleFilter);
  crackleFilter.connect(crackleGain);
  crackleGain.connect(target);
  // Secondary: dull thud — low sine sweep.
  const thud = ctx.createOscillator();
  thud.type = 'sine';
  thud.frequency.setValueAtTime(140 * variation.pitchMul, now);
  thud.frequency.exponentialRampToValueAtTime(80 * variation.pitchMul, now + 0.1);
  const thudGain = ctx.createGain();
  thudGain.gain.setValueAtTime(0.0001, now);
  thudGain.gain.exponentialRampToValueAtTime(0.32 * variation.gainMul, now + 0.005);
  thudGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.25);
  thud.connect(thudGain);
  thudGain.connect(target);
  thud.start(now);
  thud.stop(now + 0.26);
  return noise;
}

// ── Kinetic ──────────────────────────────────────────────────────────

function buildKineticFire(
  ctx: AudioContext,
  target: AudioNode,
  variation: CueVariation = NEUTRAL,
): AudioScheduledSourceNode {
  const now = ctx.currentTime;
  // Primary: percussive thump — low sine with very fast attack + decay.
  const thump = ctx.createOscillator();
  thump.type = 'sine';
  thump.frequency.setValueAtTime(180 * variation.pitchMul, now);
  thump.frequency.exponentialRampToValueAtTime(60 * variation.pitchMul, now + 0.06);
  const thumpGain = ctx.createGain();
  thumpGain.gain.setValueAtTime(0.0001, now);
  thumpGain.gain.exponentialRampToValueAtTime(0.4 * variation.gainMul, now + 0.005);
  thumpGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.12);
  thump.connect(thumpGain);
  thumpGain.connect(target);
  thump.start(now);
  thump.stop(now + 0.13);
  // Secondary: metallic tail — high-Q band-passed noise.
  const noise = createNoiseSource(ctx, 0.18);
  const tailFilter = ctx.createBiquadFilter();
  tailFilter.type = 'bandpass';
  tailFilter.frequency.value = 3500 * variation.pitchMul;
  tailFilter.Q.value = 5;
  const tailGain = ctx.createGain();
  tailGain.gain.setValueAtTime(0.0001, now);
  tailGain.gain.exponentialRampToValueAtTime(0.15 * variation.gainMul, now + 0.02);
  tailGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.18);
  noise.connect(tailFilter);
  tailFilter.connect(tailGain);
  tailGain.connect(target);
  noise.start(now);
  noise.stop(now + 0.19);
  return thump;
}

function buildKineticShield(
  ctx: AudioContext,
  target: AudioNode,
  variation: CueVariation = NEUTRAL,
): AudioScheduledSourceNode {
  const now = ctx.currentTime;
  // Metallic clang — two detuned sine tones for harmonic clash.
  const osc1 = ctx.createOscillator();
  osc1.type = 'triangle';
  osc1.frequency.value = 740 * variation.pitchMul;
  const gain1 = ctx.createGain();
  gain1.gain.setValueAtTime(0.0001, now);
  gain1.gain.exponentialRampToValueAtTime(0.3 * variation.gainMul, now + 0.005);
  gain1.gain.exponentialRampToValueAtTime(0.0001, now + 0.25);
  osc1.connect(gain1);
  gain1.connect(target);
  osc1.start(now);
  osc1.stop(now + 0.26);
  // Detuned partner for ringing texture.
  const osc2 = ctx.createOscillator();
  osc2.type = 'triangle';
  osc2.frequency.value = 1110 * variation.pitchMul;
  const gain2 = ctx.createGain();
  gain2.gain.setValueAtTime(0.0001, now);
  gain2.gain.exponentialRampToValueAtTime(0.18 * variation.gainMul, now + 0.005);
  gain2.gain.exponentialRampToValueAtTime(0.0001, now + 0.22);
  osc2.connect(gain2);
  gain2.connect(target);
  osc2.start(now);
  osc2.stop(now + 0.23);
  return osc1;
}

function buildKineticHull(
  ctx: AudioContext,
  target: AudioNode,
  variation: CueVariation = NEUTRAL,
): AudioScheduledSourceNode {
  const now = ctx.currentTime;
  // Primary: heavy thud — same shape as fire-thump but lower + longer.
  const thud = ctx.createOscillator();
  thud.type = 'sine';
  thud.frequency.setValueAtTime(120 * variation.pitchMul, now);
  thud.frequency.exponentialRampToValueAtTime(50 * variation.pitchMul, now + 0.1);
  const thudGain = ctx.createGain();
  thudGain.gain.setValueAtTime(0.0001, now);
  thudGain.gain.exponentialRampToValueAtTime(0.4 * variation.gainMul, now + 0.005);
  thudGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.18);
  thud.connect(thudGain);
  thudGain.connect(target);
  thud.start(now);
  thud.stop(now + 0.19);
  // Secondary: scrape — band-passed noise sweep.
  const noise = createNoiseSource(ctx, 0.3);
  const scrapeFilter = ctx.createBiquadFilter();
  scrapeFilter.type = 'bandpass';
  scrapeFilter.frequency.setValueAtTime(2000 * variation.pitchMul, now);
  scrapeFilter.frequency.exponentialRampToValueAtTime(800 * variation.pitchMul, now + 0.28);
  scrapeFilter.Q.value = 3;
  const scrapeGain = ctx.createGain();
  scrapeGain.gain.setValueAtTime(0.0001, now);
  scrapeGain.gain.exponentialRampToValueAtTime(0.18 * variation.gainMul, now + 0.05);
  scrapeGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.3);
  noise.connect(scrapeFilter);
  scrapeFilter.connect(scrapeGain);
  scrapeGain.connect(target);
  noise.start(now);
  noise.stop(now + 0.31);
  return thud;
}

// ── Concussion ───────────────────────────────────────────────────────

function buildConcussionFire(
  ctx: AudioContext,
  target: AudioNode,
  variation: CueVariation = NEUTRAL,
): AudioScheduledSourceNode {
  const now = ctx.currentTime;
  // Low-freq whump — single sine sweep with slow attack and longer
  // decay than kinetic. Reads as "heavier ordnance leaving the tube".
  const osc = ctx.createOscillator();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(140 * variation.pitchMul, now);
  osc.frequency.exponentialRampToValueAtTime(40 * variation.pitchMul, now + 0.18);
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.0001, now);
  gain.gain.exponentialRampToValueAtTime(0.42 * variation.gainMul, now + 0.015);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.22);
  osc.connect(gain);
  gain.connect(target);
  osc.start(now);
  osc.stop(now + 0.23);
  return osc;
}

/** Concussion impact — used for both shield and hull (splash deferred). */
function buildConcussionImpact(
  ctx: AudioContext,
  target: AudioNode,
  variation: CueVariation = NEUTRAL,
): AudioScheduledSourceNode {
  const now = ctx.currentTime;
  // Diffuse low boom — sine + low-passed noise, no sharp transient.
  const osc = ctx.createOscillator();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(90 * variation.pitchMul, now);
  osc.frequency.exponentialRampToValueAtTime(35 * variation.pitchMul, now + 0.25);
  const oscGain = ctx.createGain();
  oscGain.gain.setValueAtTime(0.0001, now);
  oscGain.gain.exponentialRampToValueAtTime(0.4 * variation.gainMul, now + 0.04);
  oscGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.3);
  osc.connect(oscGain);
  oscGain.connect(target);
  osc.start(now);
  osc.stop(now + 0.31);
  // Low-passed noise for body.
  const noise = createNoiseSource(ctx, 0.3);
  const noiseFilter = ctx.createBiquadFilter();
  noiseFilter.type = 'lowpass';
  noiseFilter.frequency.value = 400 * variation.pitchMul;
  const noiseGain = ctx.createGain();
  noiseGain.gain.setValueAtTime(0.0001, now);
  noiseGain.gain.exponentialRampToValueAtTime(0.2 * variation.gainMul, now + 0.04);
  noiseGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.3);
  noise.connect(noiseFilter);
  noiseFilter.connect(noiseGain);
  noiseGain.connect(target);
  noise.start(now);
  noise.stop(now + 0.31);
  return osc;
}

// ── EMP ──────────────────────────────────────────────────────────────

function buildEmpFire(
  ctx: AudioContext,
  target: AudioNode,
  variation: CueVariation = NEUTRAL,
): AudioScheduledSourceNode {
  const now = ctx.currentTime;
  // Electric arc crackle — high-frequency band-passed noise with fast
  // gain modulation simulating arc instability.
  const noise = createNoiseSource(ctx, 0.18);
  const filter = ctx.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.value = 4000 * variation.pitchMul;
  filter.Q.value = 8;
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.0001, now);
  gain.gain.exponentialRampToValueAtTime(0.25 * variation.gainMul, now + 0.005);
  // Two-step decay simulating arc flicker.
  gain.gain.exponentialRampToValueAtTime(0.05 * variation.gainMul, now + 0.06);
  gain.gain.exponentialRampToValueAtTime(0.18 * variation.gainMul, now + 0.09);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.18);
  noise.connect(filter);
  filter.connect(gain);
  gain.connect(target);
  return noise;
}

/** EMP impact — used for both shield and hull. v3M11 may swap hull
 *  variant for a sustained loop tied to ship.disabledTicks. */
function buildEmpImpact(
  ctx: AudioContext,
  target: AudioNode,
  variation: CueVariation = NEUTRAL,
): AudioScheduledSourceNode {
  const now = ctx.currentTime;
  // Static burst — broadband noise with quick attack.
  const noise = createNoiseSource(ctx, 0.25);
  const filter = ctx.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.value = 2000 * variation.pitchMul;
  filter.Q.value = 3;
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.0001, now);
  gain.gain.exponentialRampToValueAtTime(0.22 * variation.gainMul, now + 0.005);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.25);
  noise.connect(filter);
  filter.connect(gain);
  gain.connect(target);
  // Pitch-shifted warble — modulated sine fading in.
  const warble = ctx.createOscillator();
  warble.type = 'sine';
  const warbleLfo = ctx.createOscillator();
  warbleLfo.type = 'sine';
  warbleLfo.frequency.value = 12;
  const warbleLfoGain = ctx.createGain();
  warbleLfoGain.gain.value = 80 * variation.pitchMul;
  warbleLfo.connect(warbleLfoGain);
  warbleLfoGain.connect(warble.frequency);
  warble.frequency.value = 600 * variation.pitchMul;
  const warbleGain = ctx.createGain();
  warbleGain.gain.setValueAtTime(0.0001, now);
  warbleGain.gain.exponentialRampToValueAtTime(0.15 * variation.gainMul, now + 0.03);
  warbleGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.25);
  warble.connect(warbleGain);
  warbleGain.connect(target);
  warble.start(now);
  warble.stop(now + 0.26);
  warbleLfo.start(now);
  warbleLfo.stop(now + 0.26);
  return noise;
}

// ── Helpers ──────────────────────────────────────────────────────────

function createNoiseSource(ctx: AudioContext, durationSec: number): AudioBufferSourceNode {
  const length = Math.max(1, Math.floor(ctx.sampleRate * durationSec));
  const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < length; i++) data[i] = Math.random() * 2 - 1;
  const source = ctx.createBufferSource();
  source.buffer = buffer;
  return source;
}
